// Stamps frames with signal time. Frames carry no timestamps or sequence numbers:
// running frames come every 100 ms with network jitter, so each running frame gets previous + 100 ms,
// gently corrected towards its arrival time and re-anchored when the two drift more than 250 ms apart.
// Starting and finished frames (once a second) keep their arrival time.
// Every move into a race (starting after another phase, or a running frame not preceded by starting)
// begins a new local race number.
import type { SignalFrame, StampedFrame } from '../race/types';

export const FRAME_MS = 100;
export const REANCHOR_MS = 250;
const DRIFT_GAIN = 0.05; // share of the error corrected per frame
const DRIFT_MAX_MS = 3; // largest correction per frame

export class SignalClock {
  private prev: StampedFrame | null = null;
  private race = 0;
  private joined = new Set<number>();

  /** Races whose first frame we saw was already running. */
  joinedMidRace(raceNo: number): boolean {
    return this.joined.has(raceNo);
  }

  stamp(frame: SignalFrame, arrivalMs: number): StampedFrame {
    const prev = this.prev;
    let t: number;
    if (frame.state === 'running' && prev?.state === 'running') {
      const expected = prev.t + FRAME_MS;
      const error = arrivalMs - expected;
      t = Math.abs(error) > REANCHOR_MS
        ? Math.max(prev.t + 1, arrivalMs)
        : expected + Math.max(-DRIFT_MAX_MS, Math.min(DRIFT_MAX_MS, error * DRIFT_GAIN));
    } else {
      t = prev ? Math.max(prev.t + 1, arrivalMs) : arrivalMs;
    }

    if (frame.state === 'starting' && prev?.state !== 'starting') this.race++;
    else if (frame.state === 'running' && prev?.state !== 'running' && prev?.state !== 'starting') {
      this.race++;
      this.joined.add(this.race);
    }
    const stamped: StampedFrame = { ...frame, t, raceNo: this.race };
    this.prev = stamped;
    return stamped;
  }
}
